import {
  GenderCategory,
  ProductCatalogCategoryKey,
  ProductCatalogGender,
} from '../../__generated__/graphql/catalog/graphql'
import { getCategoryKeyFromRawCategory } from '../_components/pages/organization/constants'
import { useUser } from '../_providers/UserProvider.client'

/**
 * Converts a gender-specific shoe size to the combined "M{x}/W{y}" format.
 *
 * @param gender - The user's gender preference ("MEN" or "WOMEN")
 * @param size - The user's shoe size (e.g., "8.5")
 * @returns The combined size format (e.g., "M8.5/W10") or undefined if invalid input
 */
function convertShoeSize(
  gender: GenderCategory | undefined | null,
  size: string | undefined | null
): string | undefined {
  if (!gender || !size) return undefined

  const numericSize = parseFloat(size)
  if (isNaN(numericSize)) return undefined

  // Convert based on gender (Women's = Men's + 1.5)
  const mensSize = gender === 'MEN' ? numericSize : numericSize - 1.5
  const womensSize = gender === 'WOMEN' ? numericSize : numericSize + 1.5

  return `M${mensSize}/W${womensSize}`
}

type UseUserPreferenceForCategoryParams = {
  gender: ProductCatalogGender | undefined
  categoryKey: ProductCatalogCategoryKey | undefined
}

/**
 * Hook to find the user's matching size/fit preference for a given product category.
 * Handles unisex shoe size conversion automatically.
 *
 * @returns The raw preference object and processed size/fit IDs
 */
export const useUserPreferenceForCategory = ({
  gender,
  categoryKey,
}: UseUserPreferenceForCategoryParams) => {
  const { user } = useUser()
  const { style, size } = user?.profile.preferences ?? {}

  // For unisex products (like shoes), use the user's style preference to find their size
  // For gendered products, only apply preferences if product gender matches user's style
  const isUnisex = gender === 'unisex'
  const genderUppercase = gender?.toUpperCase() as GenderCategory | undefined
  const preferenceGender = isUnisex ? style : genderUppercase
  const preference = size?.find(
    pref =>
      pref?.gender === preferenceGender &&
      getCategoryKeyFromRawCategory(pref?.category) === categoryKey
  )

  // For unisex shoes, convert the size to the combined M/W format
  const isUnisexShoe = categoryKey === 'SHOES' && isUnisex
  const preferredSizeId = isUnisexShoe
    ? convertShoeSize(preference?.gender, preference?.size)
    : preference?.size

  return {
    preferredSizeId,
    preferredFitId: preference?.fit ?? undefined,
    preference,
  }
}
